import React, { useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { RegisterCredentials } from '../services/authService';

interface RegisterProps {
  onToggleMode: () => void;
}

const Register: React.FC<RegisterProps> = ({ onToggleMode }) => {
  const [credentials, setCredentials] = useState<RegisterCredentials>({
    name: '',
    email: '',
    password: '',
  });
  const [confirmPassword, setConfirmPassword] = useState<string>('');
  const [error, setError] = useState<string>('');
  const [loading, setLoading] = useState<boolean>(false);
  const [showPassword, setShowPassword] = useState<boolean>(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState<boolean>(false);
  const [passwordStrength, setPasswordStrength] = useState<number>(0);
  const [passwordStrengthText, setPasswordStrengthText] = useState<string>('');
  const [passwordStrengthColor, setPasswordStrengthColor] = useState<string>('');
  const [emailError, setEmailError] = useState<string>('');
  const [nameError, setNameError] = useState<string>('');

  const { register } = useAuth();

  const calculatePasswordStrength = (password: string): { strength: number; text: string; color: string } => {
    if (!password) return { strength: 0, text: '', color: '' };
    
    let strength = 0;
    const checks = {
      length: password.length >= 8,
      uppercase: /[A-Z]/.test(password),
      lowercase: /[a-z]/.test(password),
      numbers: /\d/.test(password),
      special: /[!@#$%^&*(),.?":{}|<>]/.test(password),
      veryLong: password.length >= 12
    };
    
    Object.values(checks).forEach(check => {
      if (check) strength++;
    });
    
    if (strength <= 2) return { strength, text: 'Zayıf', color: '#dc2626' };
    if (strength <= 4) return { strength, text: 'Orta', color: '#f59e0b' };
    return { strength, text: 'Güçlü', color: '#10b981' };
  };

  const validateEmail = (email: string): boolean => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!email) {
      setEmailError('E-posta alanı zorunludur');
      return false;
    }
    if (!emailRegex.test(email)) {
      setEmailError('Geçerli bir e-posta adresi girin');
      return false;
    }
    setEmailError('');
    return true;
  };

  const validateName = (name: string): boolean => {
    if (!name) {
      setNameError('Ad soyad alanı zorunludur');
      return false;
    }
    if (name.length < 3) {
      setNameError('Ad soyad en az 3 karakter olmalıdır');
      return false;
    }
    setNameError('');
    return true;
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    if (name === 'confirmPassword') {
      setConfirmPassword(value);
    } else if (name === 'password') {
      setCredentials(prev => ({
        ...prev,
        [name]: value,
      }));
      // Calculate password strength
      const strength = calculatePasswordStrength(value);
      setPasswordStrength(strength.strength);
      setPasswordStrengthText(strength.text);
      setPasswordStrengthColor(strength.color);
    } else {
      setCredentials(prev => ({
        ...prev,
        [name]: value,
      }));
    }
    setError('');
    
    // Real-time validation
    if (name === 'email') {
      validateEmail(value);
    } else if (name === 'name') {
      validateName(value);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    
    // Validate all fields
    const isNameValid = validateName(credentials.name);
    const isEmailValid = validateEmail(credentials.email);
    
    if (!isNameValid || !isEmailValid) {
      setError('Lütfen tüm alanları doğru şekilde doldurun');
      return;
    }

    if (credentials.password !== confirmPassword) {
      setError('Şifreler eşleşmiyor');
      return;
    }

    if (credentials.password.length < 6) {
      setError('Şifre en az 6 karakter olmalıdır');
      return;
    }

    setLoading(true);

    try {
      await register(credentials);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Kayıt başarısız');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="auth-container">
      <div className="auth-form">
        <h2>📈 Finans Uygulamasına Kayıt</h2>
        <form onSubmit={handleSubmit}>
          <div className="form-group">
            <label htmlFor="name">Ad Soyad</label>
            <input
              type="text"
              id="name"
              name="name"
              value={credentials.name}
              onChange={handleChange}
              required
              placeholder="Adınız Soyadınız"
              className={`form-input ${nameError ? 'error' : ''}`}
              autoComplete="name"
            />
            {nameError && <div className="field-error">{nameError}</div>}
          </div>
          <div className="form-group">
            <label htmlFor="email">E-posta</label>
            <input
              type="email"
              id="email"
              name="email"
              value={credentials.email}
              onChange={handleChange}
              required
              placeholder="ornek@email.com"
              className={`form-input ${emailError ? 'error' : ''}`}
              autoComplete="email"
            />
            {emailError && <div className="field-error">{emailError}</div>}
          </div>
          <div className="form-group">
            <label htmlFor="password">Şifre</label>
            <div className="password-input-container">
              <input
                type={showPassword ? 'text' : 'password'}
                id="password"
                name="password"
                value={credentials.password}
                onChange={handleChange}
                required
                placeholder="En az 6 karakter"
                className="form-input"
                minLength={6}
              />
              <button
                type="button"
                className="password-toggle-btn"
                onClick={() => setShowPassword(!showPassword)}
                aria-label={showPassword ? 'Şifreyi gizle' : 'Şifreyi göster'}
              >
                {showPassword ? '👁️‍🗨️' : '👁️'}
              </button>
            </div>
            {passwordStrength > 0 && (
              <div className="password-strength-indicator">
                <div className="strength-bar">
                  <div 
                    className="strength-fill"
                    style={{ 
                      width: `${(passwordStrength / 6) * 100}%`,
                      backgroundColor: passwordStrengthColor
                    }}
                  />
                </div>
                <span className="strength-text" style={{ color: passwordStrengthColor }}>
                  Şifre gücü: {passwordStrengthText}
                </span>
              </div>
            )}
          </div>
          <div className="form-group">
            <label htmlFor="confirmPassword">Şifre Tekrar</label>
            <div className="password-input-container">
              <input
                type={showConfirmPassword ? 'text' : 'password'}
                id="confirmPassword"
                name="confirmPassword"
                value={confirmPassword}
                onChange={handleChange}
                required
                placeholder="Şifrenizi tekrar girin"
                className="form-input"
                minLength={6}
              />
              <button
                type="button"
                className="password-toggle-btn"
                onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                aria-label={showConfirmPassword ? 'Şifreyi gizle' : 'Şifreyi göster'}
              >
                {showConfirmPassword ? '👁️‍🗨️' : '👁️'}
              </button>
            </div>
          </div>
          {error && <div className="error-message">{error}</div>}
          <button type="submit" disabled={loading} className="auth-button">
            {loading ? 'Kayıt Yapılıyor...' : 'Kayıt Ol'}
          </button>
        </form>
        <div className="auth-footer">
          <p>
            Zaten hesabınız var mı?{' '}
            <button type="button" onClick={onToggleMode} className="link-button">
              Giriş Yap
            </button>
          </p>
        </div>
      </div>
    </div>
  );
};

export default Register;
